import { useMemo, useCallback } from 'react';
import CollapsibleContainer, {
    CollapsibleContainerProps,
} from '@copilot/common/components/containers/collapsible';
import CampaignSummary from './campaignSummary';
import { Campaign, CampaignFields } from '@copilot/common/store/models/redux';
import CampaignSummaryHeader from './campaignHeader';
import HeaderContentLayout from '../../../../../../common/pages/layouts/headerContent';
import { useDispatch, useSelector } from 'react-redux';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import { CampaignActions } from '@copilot/common/store/actions/campaign';
import { CampaignManager } from '@copilot/data';
import { useFetch } from '@copilot/common/hooks/common';
import {
    CampaignDashboardTabKeys,
    CampaignDashboardTabLabel,
} from '@copilot/common/utils/campaign/dashboardTabs';
import { OrganizationType } from '@copilot/common/store/models/const/enum';
import { OrganizationSelectors } from '@copilot/common/store/selectors/organization';
import { loadCampaignMembersAction } from '@copilot/common/utils/campaignMember/saga';
import { Ref } from 'redux-orm';

type CampaignCollapseProps = CollapsibleContainerProps & {
    campaign: CampaignFields;
    campaignNameUpdateHandler: (id: string, name: string) => Promise<void>;
    isTeamUser: boolean;
    isAdminUser: boolean;
};

const CampaignCollapse: FCWithChildren<CampaignCollapseProps> = (props) => {
    const {
        campaign,
        isAdminUser: _isAdminUser,
        isTeamUser: _isTeamUser,
        campaignNameUpdateHandler,
        ...collapsibleContainerProps
    } = props;

    return (
        <CollapsibleContainer {...collapsibleContainerProps}>
            <CollapsibleContainer.Header>
                <CampaignSummaryHeader
                    campaign={campaign}
                    campaignNameUpdateHandler={campaignNameUpdateHandler}
                />
            </CollapsibleContainer.Header>
            <CollapsibleContainer.Content>
                <HeaderContentLayout.Tabs>
                    <HeaderContentLayout.TabPanes
                        tab={
                            <CampaignDashboardTabLabel tabKey={CampaignDashboardTabKeys.Summary} />
                        }
                        key={CampaignDashboardTabKeys.Summary}
                    >
                        <CampaignSummary campaign={campaign} />
                    </HeaderContentLayout.TabPanes>
                </HeaderContentLayout.Tabs>
            </CollapsibleContainer.Content>
        </CollapsibleContainer>
    );
};

interface CampaignCollapseListProps {
    campaigns: readonly Ref<Campaign>[];
}

const CampaignCollapseList: FCWithChildren<CampaignCollapseListProps> = (props) => {
    const { campaigns } = props;
    const storeDispatch = useDispatch();
    const activeAdmin = useSelector(OrganizationMemberSelectors.getAdminMember);
    const isTeamUser =
        useSelector(OrganizationSelectors.getActiveOrganization)?.orgType ===
        OrganizationType.Enterprise;
    const [, updateName] = useFetch(
        CampaignManager.renameCampaign,
        CampaignActions.updateCampaign,
        (r) => ({ id: r.id, name: r.name })
    );

    const nameUpdateHandler = useCallback(
        (id: string, name: string): Promise<void> => {
            updateName(id, name);
            return Promise.resolve();
        },
        [updateName]
    );

    const campaignCollapses = useMemo(
        () =>
            campaigns.map((c) => (
                <CampaignCollapse
                    key={c.id}
                    campaign={c}
                    onToggle={() => storeDispatch(loadCampaignMembersAction(c.id))}
                    isAdminUser={!!activeAdmin}
                    isTeamUser={isTeamUser}
                    style={{ margin: '20px 0' }}
                    campaignNameUpdateHandler={nameUpdateHandler}
                />
            )),
        [campaigns, activeAdmin, isTeamUser, nameUpdateHandler, storeDispatch]
    );

    return <>{campaignCollapses}</>;
};

export default CampaignCollapseList;
